import { computed, Directive, effect, inject, input } from '@angular/core';
import { cn } from '@cf/core';
import { cva, VariantProps } from 'class-variance-authority';
import { NgxSvgSpriteFragment } from 'ngxtension/svg-sprite';

export type CfIconNames =
  | 'adjust_horizontal_alt'
  | 'arrow_back'
  | 'arrow_down'
  | 'arrow_insert'
  | 'arrow_right'
  | 'block'
  | 'brush'
  | 'calendar'
  | 'calendar_new'
  | 'calendar_blank'
  | 'calendar_with_dot'
  | 'calendars'
  | 'check_circle'
  | 'check_mark'
  | 'chevron_down'
  | 'clipboard'
  | 'clock'
  | 'close'
  | 'coffee_break'
  | 'connect'
  | 'copy'
  | 'credit_card'
  | 'cross'
  | 'cross_circle'
  | 'delete'
  | 'documents'
  | 'dollar_circle'
  | 'double_arrow_back'
  | 'double_people'
  | 'drag'
  | 'edit'
  | 'embedded'
  | 'error-status'
  | 'eye_close'
  | 'eye_open'
  | 'filters'
  | 'grid'
  | 'group_people'
  | 'group_to_one'
  | 'group_to_one_sm'
  | 'has_calendar'
  | 'heart'
  | 'house'
  | 'info_circle'
  | 'location'
  | 'lock'
  | 'lock_filled'
  | 'log_out'
  | 'logout'
  | 'mail'
  | 'megaphone'
  | 'menu'
  | 'minus_circle'
  | 'minus_circle_thin'
  | 'ok-status'
  | 'options'
  | 'options_vertical'
  | 'pin'
  | 'plus'
  | 'plus_circle'
  | 'plus_circle_thin'
  | 'preview'
  | 'question_circle'
  | 'question_circle_bold'
  | 'reconnect'
  | 'search'
  | 'settings'
  | 'share'
  | 'sort_chevron'
  | 'star'
  | 'star_filled'
  | 'timezone'
  | 'triple_people'
  | 'update'
  | 'upload'
  | 'user'
  | 'users'
  | 'video'
  | 'warning'
  | 'dialog_close'
  | 'revoke'
  | 'reschedule';

const variants = cva('tw:inline-block tw:shrink-0', {
  variants: {
    size: {
      inherit: 'tw:size-inherit',
      xs: 'tw:size-[12px]',
      rem: 'tw:size-4',
      sm: 'tw:size-[18px]',
      smd: 'tw:size-[20px]',
      md: 'tw:size-[24px]',
      lg: 'tw:size-[32px]',
      xl: 'tw:size-[40px]',
      xxl: 'tw:size-[48px]',
    },
    disabled: {
      true: 'tw:pointer-events-none tw:!text-gray-40',
      false: '',
    },
  },
});

type IconVariants = VariantProps<typeof variants>;
export type IconSizeType = IconVariants['size'];

@Directive({
  selector: 'svg[cfIcon]',
  hostDirectives: [{ directive: NgxSvgSpriteFragment }],
  host: {
    '[class]': 'classes()',
    '[attr.cfIcon]': 'cfIcon()',
  },
})
export class CfIconDirective {
  class = input<string>('');
  cfIcon = input<CfIconNames>();
  size = input<IconSizeType>('md');
  disabled = input<IconVariants['disabled']>(false);

  classes = computed(() =>
    cn(variants({ size: this.size() ?? 'md', disabled: this.disabled() ?? false }), this.class()),
  );

  constructor() {
    const fragment = inject(NgxSvgSpriteFragment);
    fragment.sprite = 'icons';
    effect(() => {
      const icon = this.cfIcon();
      if (icon) {
        fragment.fragment = icon;
      }
    });
  }
}
